import { useMemo } from "react"
import { EffectTarget, EFFECT_DIGGING_TYPES } from "@/store/apis/effectsAPI"
import { ICardProps } from "../types"

type IUseCardAvailabilityParams = Pick<
  ICardProps,
  "data" | "player" | "owner" | "isMatchActive"
>

export const useCardAvailability = ({
  data,
  player,
  owner,
  isMatchActive
}: IUseCardAvailabilityParams) => {
  const isCanPlay = useMemo(() => {
    if (!player || !data) return false

    if (player.freezingMovements > 0) return false

    const isSkippingDigging = player.skipDiggingMovements > 0

    if (isSkippingDigging) {
      const hasSelfOrBothDiggingEffect = data.effects.some((effect) => {
        const targetsSelf =
          effect.playerTarget === EffectTarget.SELF ||
          effect.playerTarget === EffectTarget.BOTH

        const isDiggingEffect = EFFECT_DIGGING_TYPES.has(effect.type)

        return targetsSelf && isDiggingEffect && effect.value < 0
      })

      if (hasSelfOrBothDiggingEffect) {
        return false
      }
    }

    return true
  }, [player, data])

  const isEnough = useMemo(() => {
    if (!data?.price || !data?.currency || !player) return false

    return data.price <= player[data.currency]
  }, [data, player])

  const isAvailableToUse = useMemo(() => {
    return isCanPlay && isEnough
  }, [isCanPlay, isEnough])

  const isShowActive = useMemo(() => {
    if (owner !== "user" || !isMatchActive) return true

    return isAvailableToUse
  }, [isAvailableToUse, owner, isMatchActive])

  return {
    isCanPlay,
    isEnough,
    isAvailableToUse,
    isShowActive
  }
}
